import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Plus, AlertTriangle, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/api/apiClient';
import { cur } from '@/lib/currency';
function AgingBar({ label, amount, total, color }) {
  const pct = total > 0 ? Math.round((amount / total) * 100) : 0;
  const colorMap = {
    green: 'bg-emerald-500',
    amber: 'bg-amber-500',
    orange: 'bg-orange-500',
    red: 'bg-red-600',
    darkred: 'bg-red-800',
  };
  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="text-xs font-bold text-foreground">{Number(amount).toLocaleString('ar-EG', { maximumFractionDigits: 0 })} {cur()}</span>
      </div>
      <div className="w-full bg-muted rounded-full h-2">
        <div className={`h-2 rounded-full ${colorMap[color]} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const PAYMENT_STATE_MAP = {
  not_paid: { label: 'غير مدفوع', color: 'text-red-600 bg-red-50 border border-red-200' },
  partial: { label: 'جزئي', color: 'text-amber-600 bg-amber-50 border border-amber-200' },
  in_payment: { label: 'قيد المعالجة', color: 'text-blue-600 bg-blue-50 border border-blue-200' },
  paid: { label: 'مدفوع', color: 'text-emerald-600 bg-emerald-50 border border-emerald-200' },
};

export default function PaymentsTab({ customerId, customerName }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customerId) return;
    const load = async () => {
      setLoading(true);
      const res = await api.get(`/collections/customer/${customerId}/invoices`);
      if (res?.success) {
        setData({
          invoices: res.data || [],
          summary: res.summary || {},
          customer: res.customer || {},
          // No aging is returned per-customer by default; leave empty.
          aging: {},
        });
      }
      setLoading(false);
    };
    load();
  }, [customerId]);

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );

  const invoices = data?.invoices || [];
  const outstanding = invoices.filter(i => i.amount_residual > 0);
  const totalOutstanding = outstanding.reduce((s, i) => s + i.amount_residual, 0);
  const aging = data?.aging || {};
  const agingTotal = totalOutstanding || 1;

  return (
    <div className="space-y-4">
      {/* New collection button */}
      <Button
        onClick={() => navigate(`/collections/new?customer_id=${customerId}&customer_name=${encodeURIComponent(customerName)}`)}
        className="w-full bg-secondary gap-2"
      >
        <Plus className="w-4 h-4" /> تحصيل جديد
      </Button>

      {/* Outstanding summary */}
      {totalOutstanding > 0 ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 space-y-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <p className="font-bold text-red-700 text-lg">{Number(totalOutstanding).toLocaleString('ar-EG', { maximumFractionDigits: 0 })} {cur()}</p>
              <p className="text-xs text-red-600">{outstanding.length} فاتورة غير مسددة</p>
            </div>
          </div>
          {/* Aging */}
          <div className="space-y-2 pt-2 border-t border-red-200">
            <p className="text-xs font-semibold text-red-700 mb-2">تحليل التقادم</p>
            {aging.current > 0 && <AgingBar label="0-30 يوم (جاري)" amount={aging.current} total={agingTotal} color="green" />}
            {aging.days30 > 0 && <AgingBar label="31-60 يوم" amount={aging.days30} total={agingTotal} color="amber" />}
            {aging.days60 > 0 && <AgingBar label="61-90 يوم" amount={aging.days60} total={agingTotal} color="orange" />}
            {aging.over90 > 0 && <AgingBar label="أكثر من 90 يوم 🚨" amount={aging.over90} total={agingTotal} color="darkred" />}
          </div>
        </div>
      ) : (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center gap-3">
          <CheckCircle2 className="w-8 h-8 text-emerald-600 flex-shrink-0" />
          <div>
            <p className="font-bold text-emerald-700">لا توجد متأخرات</p>
            <p className="text-xs text-emerald-600">جميع الفواتير مسددة</p>
          </div>
        </div>
      )}

      {/* Invoices list */}
      {invoices.length === 0 ? (
        <p className="text-center text-muted-foreground py-10 text-sm">لا توجد فواتير</p>
      ) : (
        <div className="space-y-2">
          <h3 className="text-sm font-bold text-foreground">الفواتير ({invoices.length})</h3>
          {invoices.map(inv => {
            const ps = PAYMENT_STATE_MAP[inv.payment_state] || { label: inv.payment_state, color: 'bg-muted text-muted-foreground' };
            return (
              <div key={inv.id} className="bg-card border border-border rounded-2xl p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground text-sm">{inv.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      تاريخ الإصدار: {inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('ar-EG') : '—'}
                    </p>
                    {inv.invoice_date_due && (
                      <p className={`text-xs mt-0.5 ${inv.days_overdue > 0 ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`}>
                        الاستحقاق: {new Date(inv.invoice_date_due).toLocaleDateString('ar-EG')}
                        {inv.days_overdue > 0 && ` (متأخر ${inv.days_overdue} يوم)`}
                      </p>
                    )}
                  </div>
                  <div className="text-left flex-shrink-0 space-y-1">
                    <p className="font-bold text-foreground text-sm">{Number(inv.amount_total).toLocaleString('ar-EG', { maximumFractionDigits: 0 })} {cur()}</p>
                    {inv.amount_residual > 0 && (
                      <p className="text-xs font-bold text-red-600">{Number(inv.amount_residual).toLocaleString('ar-EG', { maximumFractionDigits: 0 })} {cur()} متبقي</p>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full inline-block ${ps.color}`}>{ps.label}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}